// src/context/TeamSettingsContext.jsx
// Manages team-level SOP feature toggles and operational settings.
// Only the team Owner loads/saves; members read the owner's settings.

import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from './AuthContext';
import { logger } from '../utils/logger';

const TeamSettingsContext = createContext(null);

// ── Default settings (everything OFF = "Light" preset) ────────
const DEFAULT_SETTINGS = {
  teamSize: 'solo',
  sopPreset: 'light',
  features: {
    // Approval workflows
    requireConversionApproval: false,
    requireBudgetApproval: false,
    budgetApprovalThreshold: 10,
    requireListingApproval: false,
    requirePriceChangeApproval: false,
    requireSaleApproval: false,
    approvalRoles: ['Owner', 'Admin'],

    // Project gates
    enableCompletionGate: false,
    completionGateThreshold: 90,
    completionGateMinMargin: 15,
    enableDocumentCompliance: false,

    // Risk monitoring
    enableRiskAlerts: false,
    riskAlertBudgetOverrun: 10,
    riskAlertMaxDOM: 45,
    riskAlertMaxHoldDays: 120,
    riskAlertMinROI: 15,

    // Financial tracking
    enableCapitalTracking: false,
    enablePostSaleVariance: false,

    // Operational automation
    enableWeeklyReviews: false,
    enableListingPerformance: false,

    // Underwriting
    enableMAOOnLeads: false,
    enableHistoricalBenchmarks: false,

    // Deal Pipeline (acquisition approval workflow)
    enableDealPipeline: false,
    requireCFOReview: true,
    requireCEOApproval: true,
    requireFundingConfirmation: true,
  },
};

// ── Preset configurations ──────────────────────────────────────
const PRESETS = {
  light: {
    ...DEFAULT_SETTINGS.features,
  },
  standard: {
    ...DEFAULT_SETTINGS.features,
    requireBudgetApproval: true,
    budgetApprovalThreshold: 10,
    enableCompletionGate: true,
    completionGateThreshold: 90,
    completionGateMinMargin: 15,
    enableRiskAlerts: true,
    riskAlertBudgetOverrun: 10,
    riskAlertMaxDOM: 45,
    riskAlertMaxHoldDays: 120,
    riskAlertMinROI: 15,
    enableCapitalTracking: true,
    enableDealPipeline: true,
    requireCFOReview: true,
    requireCEOApproval: true,
    requireFundingConfirmation: true,
  },
  full: {
    ...DEFAULT_SETTINGS.features,
    requireConversionApproval: true,
    requireBudgetApproval: true,
    budgetApprovalThreshold: 10,
    requireListingApproval: true,
    requirePriceChangeApproval: true,
    requireSaleApproval: true,
    approvalRoles: ['Owner', 'Admin'],
    enableCompletionGate: true,
    completionGateThreshold: 90,
    completionGateMinMargin: 15,
    enableDocumentCompliance: true,
    enableRiskAlerts: true,
    riskAlertBudgetOverrun: 10,
    riskAlertMaxDOM: 45,
    riskAlertMaxHoldDays: 120,
    riskAlertMinROI: 15,
    enableCapitalTracking: true,
    enablePostSaleVariance: true,
    enableWeeklyReviews: true,
    enableListingPerformance: true,
    enableMAOOnLeads: true,
    enableHistoricalBenchmarks: true,
    enableDealPipeline: true,
    requireCFOReview: true,
    requireCEOApproval: true,
    requireFundingConfirmation: true,
  },
};

export function TeamSettingsProvider({ children }) {
  const { firebaseUser, teamOwnerId } = useAuth();

  const [teamSettings, setTeamSettings] = useState(DEFAULT_SETTINGS);
  const [settingsLoading, setSettingsLoading] = useState(true);

  // Determine whose settings to load (owner's settings always)
  const ownerId = teamOwnerId || firebaseUser?.uid;

  // ── Load settings from Firestore ────────────────────────────
  useEffect(() => {
    if (!ownerId) {
      setSettingsLoading(false);
      return;
    }

    const loadSettings = async () => {
      try {
        const docSnap = await getDoc(doc(db, 'teamSettings', ownerId));
        if (docSnap.exists()) {
          const data = docSnap.data();
          setTeamSettings({
            teamSize: data.teamSize || DEFAULT_SETTINGS.teamSize,
            sopPreset: data.sopPreset || DEFAULT_SETTINGS.sopPreset,
            features: { ...DEFAULT_SETTINGS.features, ...data.features },
          });
        }
        // If no doc exists, defaults remain (Light preset)
      } catch (error) {
        logger.error('Error loading team settings:', error);
      }
      setSettingsLoading(false);
    };

    loadSettings();
  }, [ownerId]);

  // ── Save settings to Firestore ──────────────────────────────
  const saveTeamSettings = useCallback(
    async (newSettings) => {
      if (!ownerId) return { success: false, error: 'No owner ID' };

      try {
        const settingsToSave = {
          teamSize: newSettings.teamSize,
          sopPreset: newSettings.sopPreset,
          features: newSettings.features,
          updatedAt: serverTimestamp(),
          updatedBy: firebaseUser?.uid || null,
        };

        await setDoc(doc(db, 'teamSettings', ownerId), settingsToSave, { merge: true });
        setTeamSettings(newSettings);
        return { success: true };
      } catch (error) {
        logger.error('Error saving team settings:', error);
        return { success: false, error: error.message };
      }
    },
    [ownerId, firebaseUser]
  );

  // ── Apply a preset ──────────────────────────────────────────
  const applyPreset = useCallback(
    (presetName) => {
      const presetFeatures = PRESETS[presetName];
      if (!presetFeatures) return;

      return {
        ...teamSettings,
        sopPreset: presetName,
        features: { ...presetFeatures },
      };
    },
    [teamSettings]
  );

  // ── Check if a specific feature is enabled ──────────────────
  const isFeatureEnabled = useCallback(
    (featureName) => {
      return teamSettings.features[featureName] === true;
    },
    [teamSettings]
  );

  // ── Get a threshold value ───────────────────────────────────
  const getThreshold = useCallback(
    (thresholdName) => {
      return teamSettings.features[thresholdName] ?? DEFAULT_SETTINGS.features[thresholdName];
    },
    [teamSettings]
  );

  // ── Count active features ───────────────────────────────────
  const activeFeatureCount = useMemo(() => {
    const booleanKeys = Object.entries(teamSettings.features).filter(([, val]) => val === true);
    return booleanKeys.length;
  }, [teamSettings]);

  // ── Context value ───────────────────────────────────────────
  const value = useMemo(
    () => ({
      teamSettings,
      setTeamSettings,
      saveTeamSettings,
      applyPreset,
      isFeatureEnabled,
      getThreshold,
      settingsLoading,
      activeFeatureCount,
      PRESETS,
      DEFAULT_SETTINGS,
    }),
    [
      teamSettings,
      saveTeamSettings,
      applyPreset,
      isFeatureEnabled,
      getThreshold,
      settingsLoading,
      activeFeatureCount,
    ]
  );

  return <TeamSettingsContext.Provider value={value}>{children}</TeamSettingsContext.Provider>;
}

export function useTeamSettings() {
  const context = useContext(TeamSettingsContext);
  if (!context) throw new Error('useTeamSettings must be used within TeamSettingsProvider');
  return context;
}
